import classNames from 'classnames';
import { ComponentType, ReactElement } from 'react';
import { Loader, formatCount } from '@sc/shared-ui';
import { Sparkline } from './Sparkline';

interface Props {
  name: string;
  count?: number | undefined;
  comparator?: number | undefined;
  loading?: boolean | undefined;
  icon?: ComponentType<{ className?: string }> | undefined;
  sparkline?: number[] | undefined;
  deltaLabel?: string | undefined;
}

export const Metric = ({
  name,
  count = 0,
  comparator = 0,
  loading,
  icon: Icon,
  sparkline,
  deltaLabel = 'MOM',
}: Props): ReactElement => {
  // With no baseline to compare against we still show a delta line (+0%),
  // rather than hiding it, so every card keeps the same layout.
  const adjustment =
    comparator === 0 ? '0.00' : ((count / comparator - 1) * 100).toFixed(2);

  const growth = count >= comparator;

  return (
    <div className="bg-background-tertiary border border-grey-t-800 rounded-md p-5 flex flex-col gap-2">
      <div className="flex items-center gap-2">
        {Icon && <Icon className="size-5 text-grey-400 shrink-0" />}
        <h4 className="text-h4 font-grotesque font-semibold text-grey-400 [text-box-trim:trim-both] [text-box-edge:cap_alphabetic]">
          {name}
        </h4>
        {loading && <Loader className="size-5" />}
      </div>
      {!loading && (
        <>
          <div className="flex items-center gap-3">
            <span className="text-h1-display text-white flex-1 min-w-0">
              {formatCount(count)}
            </span>
            {sparkline && (
              <Sparkline data={sparkline} className="w-2/5 h-11 shrink-0" />
            )}
          </div>
          <span
            className={classNames('text-body-smallest font-semibold', {
              'text-primary-400': growth,
              'text-red-400': !growth,
            })}
          >
            {growth && '+'}
            {adjustment}% {deltaLabel}
          </span>
        </>
      )}
    </div>
  );
};
